import uuid from 'uuid-random';

import {ActionBase, ActionWithPayload} from '../../helpers/reduxHelpers';
import {AppAction} from '../appActions';
import {ThunkAsyncAction} from '../appThunk';
import {
  OptimisticUpdatesFinishIdsActionExtension,
  optimisticUpdatesFinishIdsKey,
} from './optimisticUpdatesHelpers';
import {OptimisticUpdateId, OptimisticUpdater} from './optimisticUpdatesState';

/*
 * Action types.
 */

export enum OptimisticUpdateActionsEnum {
  START_OPTIMISTIC_UPDATE = '@@optimisticUpdates/START_OPTIMISTIC_UPDATE',
  FINISH_OPTIMISTIC_UPDATE = '@@optimisticUpdates/FINISH_OPTIMISTIC_UPDATE',
}

/*
 * Types.
 */

export interface StartOptimisticUpdateActionPayload {
  id: OptimisticUpdateId;
  updater: OptimisticUpdater;
}

export interface StartOptimisticUpdateAction extends ActionWithPayload<StartOptimisticUpdateActionPayload> {
  type: OptimisticUpdateActionsEnum.START_OPTIMISTIC_UPDATE;
}

export interface FinishOptimisticUpdateAction extends ActionBase, OptimisticUpdatesFinishIdsActionExtension {
  type: OptimisticUpdateActionsEnum.FINISH_OPTIMISTIC_UPDATE;
}

export type OptimisticUpdatesAction = StartOptimisticUpdateAction | FinishOptimisticUpdateAction;

/*
 * Action creators.
 */

export function startOptimisticUpdateAction(
  id: OptimisticUpdateId,
  updater: OptimisticUpdater,
): StartOptimisticUpdateAction {
  return {
    type: OptimisticUpdateActionsEnum.START_OPTIMISTIC_UPDATE,
    payload: {
      id,
      updater,
    },
  };
}

/** Upgrades the provided action such that it also finishes a corresponding optimistic update. */
export function finishOptimisticUpdateAction(
  id: OptimisticUpdateId,
  resultAction?: AppAction,
): AppAction & OptimisticUpdatesFinishIdsActionExtension {
  // Use either the provided action or a no-op action as a carrier.
  const baseAction = resultAction || {type: OptimisticUpdateActionsEnum.FINISH_OPTIMISTIC_UPDATE};

  return {
    ...baseAction,
    [optimisticUpdatesFinishIdsKey]: [id],
  };
}

export function optimisticUpdateAsyncAction(
  updater: OptimisticUpdater,
  performer: () => Promise<AppAction | undefined>,
): ThunkAsyncAction {
  const id = uuid();

  return async (dispatch) => {
    dispatch(startOptimisticUpdateAction(id, updater));
    let resultAction: AppAction | undefined;
    try {
      resultAction = await performer();
    } finally {
      dispatch(finishOptimisticUpdateAction(id, resultAction));
    }
  };
}
